import { EnergyBusFragment } from '../generated/graphql'
import { reduceToSumOfItems } from '../util/array-utility'
import { getValuesFromProviders } from './calculator-values-utility'
import { balanceErrorThreshold } from './providers/common/Provider'
import {
  HybridHeatPumpProvider,
  isHybridHeatPumpProvider,
} from './providers/HybridHeatPumpProvider'
import { SystemContainerCalculator } from './SystemContainerCalculator'
import {
  EnergyBusResultKey,
  energyBusSourceHeatingSubCategory,
  energyBusSourceCoolingSubCategory,
} from '../provider/result-categories'

export const heatInKeys = [
  EnergyBusResultKey.HeatFromEnvironment,
  EnergyBusResultKey.WasteEnergy,
  EnergyBusResultKey.CoolingFromEnergyBus,
]
export const heatOutKeys = [
  EnergyBusResultKey.HeatFromEnergyBus,
  EnergyBusResultKey.HeatToEnvironment,
]

type EnergyBusResultValues = Record<EnergyBusResultKey, number[]>

function calculateEnergyBusResultValues(
  heatingValues: number[],
  coolingValues: number[],
  availableWasteValues?: number[],
): EnergyBusResultValues {
  if (heatingValues.length !== coolingValues.length) {
    throw new Error(
      'Invalid input when calculating energy bus result values: length mismatch',
    )
  }

  const heatFromEnvironment: number[] = []
  const wasteEnergy: number[] = []
  const coolingFromEnergyBus = coolingValues
  const heatFromEnergyBus = heatingValues
  const heatToEnvironment: number[] = []

  heatingValues.forEach((heating, i) => {
    const availableWaste = availableWasteValues ? availableWasteValues[i] : 0
    const cooling = coolingValues[i]

    const excessHeating = Math.max(heating - cooling, 0)
    const excessCooling = Math.max(cooling - heating, 0)

    if (availableWasteValues) {
      wasteEnergy[i] = Math.min(excessHeating, availableWaste)
    }

    heatFromEnvironment[i] = Math.max(excessHeating - availableWaste, 0)
    heatToEnvironment[i] = excessCooling
  })

  return {
    heatFromEnvironment,
    wasteEnergy,
    coolingFromEnergyBus,
    heatFromEnergyBus,
    heatToEnvironment,
  }
}

export class EnergyBusCalculator {
  private readonly providers: HybridHeatPumpProvider[] = []

  private resultValues: EnergyBusResultValues | null = null

  private hasProvided = false

  public constructor(
    public readonly id: string,
    private readonly wasteHourSeriesValues?: number[],
  ) {
    this.wasteHourSeriesValues = wasteHourSeriesValues
  }

  public addProvider(provider: HybridHeatPumpProvider): void {
    this.providers.push(provider)
  }

  public calculateUsage(): void {
    const energyBusSourceHeatingValues = getValuesFromProviders(
      this.providers,
      energyBusSourceHeatingSubCategory,
    )

    const energyBusSourceCoolingValues = getValuesFromProviders(
      this.providers,
      energyBusSourceCoolingSubCategory,
    )

    const energyBusValues = calculateEnergyBusResultValues(
      energyBusSourceHeatingValues,
      energyBusSourceCoolingValues,
      this.wasteHourSeriesValues,
    )

    this.resultValues = energyBusValues

    this.hasProvided = true
  }

  public checkBalance(): void {
    if (!this.hasProvided) {
      throw new Error('Please calculate usage before checking balance.')
    }

    this.checkTotalBalance()
  }

  public getResultValues(): EnergyBusResultValues {
    if (!this.resultValues) {
      throw new Error('Check calculation order!')
    }

    return this.resultValues
  }

  private checkTotalBalance(): void {
    const resultValues = this.getResultValues()

    const incomingSeries = heatInKeys
      .map((key) => resultValues[key])
      .reduce(reduceToSumOfItems, [])
    const outgoingSeries = heatOutKeys
      .map((key) => resultValues[key])
      .reduce(reduceToSumOfItems, [])

    if (incomingSeries.length !== outgoingSeries.length) {
      throw new Error('Data length mismatch when checking building balance')
    }

    incomingSeries.forEach((incoming, hour) => {
      if (Math.abs(incoming - outgoingSeries[hour]) > balanceErrorThreshold) {
        throw new Error(
          `Error in building balance: in ${incoming}, out ${outgoingSeries[hour]} for hour ${hour}`,
        )
      }
    })
  }
}

export function createMapEnergyBusToEnergyBusCalculator(
  systemContainers: SystemContainerCalculator[],
) {
  return (energyBus: EnergyBusFragment): EnergyBusCalculator => {
    const energyBusCalculator = new EnergyBusCalculator(
      energyBus.id,
      energyBus.wasteHourSeries?.values,
    )

    const providers = systemContainers
      .flatMap((systemContainer) => systemContainer.getProviders())
      .filter(isHybridHeatPumpProvider)

    providers.forEach((provider) => {
      energyBusCalculator.addProvider(provider)
    })

    return energyBusCalculator
  }
}
